import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from '../../api.js';
import { t } from '../../i18n/index.js';
import { ConfirmDialog } from '../../components/ConfirmDialog.js';
import type { AdminGameView, ChatMessage } from '../../../shared/protocol.js';

type Flash = { ok: boolean; text: string } | null;

const errorText = (err: unknown) => t.errors[err instanceof ApiError ? err.code : 'network'];

/** Active games: inspect, read the match chat (decision 2026-09-27) and abort. */
export function GamesAdmin() {
  const [games, setGames] = useState<AdminGameView[]>([]);
  const [flash, setFlash] = useState<Flash>(null);
  const [chat, setChat] = useState<{ gameId: number; messages: ChatMessage[] } | null>(null);
  const [aborting, setAborting] = useState<number | null>(null);

  const load = () =>
    api<{ games: AdminGameView[] }>('GET', '/api/admin/games')
      .then((r) => setGames(r.games))
      .catch((err) => setFlash({ ok: false, text: errorText(err) }));

  useEffect(() => {
    void load();
  }, []);

  const showChat = async (gameId: number) => {
    if (chat?.gameId === gameId) return setChat(null);
    try {
      const r = await api<{ messages: ChatMessage[] }>('GET', `/api/admin/games/${gameId}/chat`);
      setChat({ gameId, messages: r.messages });
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  const abort = async (gameId: number) => {
    setAborting(null);
    try {
      await api('POST', `/api/admin/games/${gameId}/abort`);
      setFlash({ ok: true, text: t.admin.games.aborted });
      await load();
    } catch (err) {
      setFlash({ ok: false, text: errorText(err) });
    }
  };

  return (
    <section class="card">
      <div class="section-head">
        <h2>{t.admin.games.title}</h2>
        <button class="btn btn-small" onClick={() => void load()}>
          {t.admin.games.refresh}
        </button>
      </div>
      {flash && (
        <p class={flash.ok ? 'success' : 'error'} role="status">
          {flash.text}
        </p>
      )}
      {games.length === 0 ? (
        <p class="muted">{t.admin.games.none}</p>
      ) : (
        <ul class="user-list">
          {games.map((g) => (
            <li class="user-row">
              <div class="user-main">
                <div class="user-name">
                  #{g.id} {t.games[g.gameType].name} · {t.admin.games.status[g.status]}
                </div>
                <div class="user-sub">
                  {g.players.map((p) => p.displayName + (p.isAi ? ` (${t.admin.games.computer})` : '')).join(' – ')} ·{' '}
                  {t.admin.games.started}{' '}
                  {new Date(g.createdAt).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' })}
                </div>
              </div>
              <div class="user-actions">
                <button class="btn btn-small" onClick={() => void showChat(g.id)}>
                  {chat?.gameId === g.id ? t.admin.games.hideChat : t.admin.games.showChat}
                </button>
                <button class="btn btn-small btn-danger" onClick={() => setAborting(g.id)}>
                  {t.admin.games.abort}
                </button>
              </div>
              {chat?.gameId === g.id && (
                <div class="admin-chat">
                  {chat.messages.length === 0 ? (
                    <p class="muted">{t.admin.games.noChat}</p>
                  ) : (
                    chat.messages.map((m) => (
                      <div class="chat-msg">
                        <div class="chat-meta">
                          {m.displayName} · {new Date(m.sentAt).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div class="chat-text">{m.text}</div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      {aborting !== null && (
        <ConfirmDialog
          title={t.admin.games.abortConfirmTitle}
          confirmLabel={t.admin.games.abort}
          cancelLabel={t.admin.cancel}
          danger
          onConfirm={() => void abort(aborting)}
          onCancel={() => setAborting(null)}
        >
          <p>{t.admin.games.abortConfirmText}</p>
        </ConfirmDialog>
      )}
    </section>
  );
}
